/**
 * OccupancyStrip — 14 Tage Belegung als kompakter Streifen, ein Block je Tag.
 *
 * Signaturelement der Apartmentliste: eine Zeile zeigt ohne Umweg, wie die nächsten zwei Wochen
 * aussehen. Belegte Nächte in Sage, freie in der ruhigen Nebenfläche, heute mit einem Strich
 * darüber. Ohne Daten (noch am Laden) steht ein abgeblendeter Streifen und kein Prozentwert —
 * eine Zahl wäre dort eine Behauptung.
 */
import type { CalendarDayCell } from '@/shared/api';

const DAY_COUNT = 14;
const BLOCK_W = 8;
const GAP = 2;
const BLOCK_H = 18;
const TODAY_MARK_H = 2;

interface OccupancyStripProps {
  cells: CalendarDayCell[] | undefined;
  fromDate: string;
}

export function OccupancyStrip({ cells, fromDate }: OccupancyStripProps) {
  const totalW = DAY_COUNT * BLOCK_W + (DAY_COUNT - 1) * GAP;
  const totalH = BLOCK_H + TODAY_MARK_H + 2;
  const todayKey = new Date().toISOString().slice(0, 10);

  const byDate = new Map<string, CalendarDayCell>();
  for (const c of cells ?? []) byDate.set(c.date, c);

  const days = Array.from({ length: DAY_COUNT }, (_, i) => {
    const d = new Date(fromDate);
    d.setUTCDate(d.getUTCDate() + i);
    return d.toISOString().slice(0, 10);
  });

  const booked = days.filter((d) => byDate.get(d)?.status === 'booked').length;
  const occupancyPct = Math.round((booked / DAY_COUNT) * 100);

  return (
    <div className="inline-flex flex-col gap-1">
      <svg
        width={totalW}
        height={totalH}
        viewBox={`0 0 ${totalW} ${totalH}`}
        role="img"
        aria-label={
          cells
            ? `Belegung der nächsten ${DAY_COUNT} Tage: ${booked} von ${DAY_COUNT} Nächten belegt, ${occupancyPct} Prozent`
            : 'Belegung wird geladen'
        }
        className="block"
      >
        {days.map((date, i) => {
          const cell = byDate.get(date);
          const isBooked = cell?.status === 'booked';
          const isToday = date === todayKey;
          const x = i * (BLOCK_W + GAP);
          return (
            <g key={date}>
              {isToday && (
                <rect
                  x={x}
                  y={0}
                  width={BLOCK_W}
                  height={TODAY_MARK_H}
                  className="fill-ink-faint"
                  rx={1}
                />
              )}
              <rect
                x={x}
                y={TODAY_MARK_H + 2}
                width={BLOCK_W}
                height={BLOCK_H}
                rx={1.5}
                className={isBooked ? 'fill-sage' : 'fill-card-alt'}
                opacity={cells ? 1 : 0.4}
              />
            </g>
          );
        })}
      </svg>
      <span className="text-ink-soft text-[10px] uppercase tracking-wider">
        {cells ? `${occupancyPct}% belegt` : 'wird geladen'}
      </span>
    </div>
  );
}
